import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { $url } from "utils/url";
import { ResetPasswordModal } from "views/Staff/components/ResetPasswordModal";
import { BannerModal } from "views/Banner/Components/BannerModal";
import { Banner } from "types/banner";
import { bannerApi } from "api/banner.api";

export const BannerPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Banner[]>([]);
  const [total, setTotal] = useState(0);

  const resetPasswordModalRef = useRef<ResetPasswordModal>(null);
  const bannerModalRef = useRef<BannerModal>(null);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await bannerApi.findAll(queryParams);
      setData(res.data.banners);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (row: Banner) => {
      setLoading(true);
      try {
        await bannerApi.delete(row.id);
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const columns = useMemo<TableColumnsType<Banner>>(
    () => [
      {
        title: "Tên banner",
        dataIndex: "name",
        key: "name",
        render: (value, record) => (
          <div className="space-y-1 min-w-0">
            <INA image={record.image ? $url(record.image) : undefined} name={value || "--"} />
          </div>
        ),
      },
      {
        title: "Vị trí",
        dataIndex: "position",
        key: "position",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Hoạt động",
        dataIndex: "isActive",
        key: "isActive",
        render: (value) => <TableContent>{value ? "Hoạt động" : "Không hoạt động"}</TableContent>,
      },
      {
        title: "Thao tác",
        key: "action",
        width: 160,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  bannerModalRef.current?.handleUpdate(record);
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                cancelText="Không"
                okText="Xóa"
                onConfirm={() => handleDelete(record)}
                title="Xác nhận xóa?"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleDelete],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm theo tên banner",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  bannerModalRef.current?.handleCreate();
                }}
              >
                Tạo mới
              </AppButton>
            }
            // actions={
            //   <Popconfirm
            //     cancelText="Không"
            //     okText="Khóa"
            //     onConfirm={() => handleBlock(selectedStaffs, true)}
            //     title="Xác nhận khóa?"
            //   >
            //     <AppButton
            //       tone="light"
            //       icon={<LockOutlined />}
            //       disabled={!selectedRowKeys.length}
            //       loading={loading}
            //     >
            //       Khóa
            //     </AppButton>
            //   </Popconfirm>
            // }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Banner>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            scroll={{ x: 1100 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              setQuery((prev) => ({ ...prev, page, limit }));
            }}
          />
        }
      />

      <ResetPasswordModal
        ref={resetPasswordModalRef}
        onClose={() => {}}
        onSubmitOk={() => fetchData(query)}
      />

      <BannerModal onClose={() => {}} onSubmitOk={() => fetchData(query)} ref={bannerModalRef} />
    </div>
  );
};
